import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import ModalHeader from 'react-bootstrap/ModalHeader';
import ModalTitle from 'react-bootstrap/ModalTitle';
import ModalBody from 'react-bootstrap/ModalBody';
import ModalFooter from 'react-bootstrap/ModalFooter';
import { FaFolder, FaFolderOpen, FaFileAlt, FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import Api, { exceptionNotificationAPI } from '../../services/api';
import { success_message } from '../../components/Toast';
import {
    TreeShell,
    TreeHeaderBar,
    TreeHeaderTitle,
    TreeHeaderActions,
    TreeHeaderIconBtn,
    TreeBodyPanel,
    TreeRow,
    TreeRowMain,
    TreeExpandSlot,
    TreeExpandBtn,
    TreeIconSlot,
    TreeLabel,
    TreeActionsRail,
    TypePill,
    IconBtn,
    EmptyTreeHint,
} from './styles';

function itemId(row) {
    return row.listTreeItemID ?? row.ListTreeItemID;
}

function itemValue(row) {
    return row.value ?? row.Value ?? '';
}

function itemParentId(row) {
    const p = row.parentID ?? row.ParentID;
    return p == null ? null : p;
}

function itemCode(row) {
    return row.code ?? row.Code ?? null;
}

function itemAttrs(row) {
    return row.attrs ?? row.Attrs ?? {};
}

function collectDescendants(items, rootId) {
    const byParent = new Map();
    items.forEach(row => {
        const pid = itemParentId(row);
        if (pid == null) return;
        if (!byParent.has(pid)) byParent.set(pid, []);
        byParent.get(pid).push(itemId(row));
    });
    const out = new Set();
    const stack = [rootId];
    while (stack.length) {
        const id = stack.pop();
        if (out.has(id)) continue;
        out.add(id);
        const kids = byParent.get(id) || [];
        kids.forEach(k => stack.push(k));
    }
    return out;
}

function pillText(row) {
    const c = itemCode(row);
    if (c != null && String(c).trim() !== '') return String(c).trim();
    const attrs = itemAttrs(row);
    const keys = Object.keys(attrs);
    const funcKey = keys.find(k => k.toLowerCase() === 'functionality');
    if (funcKey && attrs[funcKey]) return String(attrs[funcKey]).trim();
    if (keys.length > 0) {
        const first = keys.sort()[0];
        if (attrs[first]) return `${first}: ${attrs[first]}`.slice(0, 40);
    }
    return null;
}

function attrsToRows(attrs) {
    const o = attrs && typeof attrs === 'object' ? attrs : {};
    const entries = Object.entries(o);
    if (entries.length === 0) return [{ name: '', value: '' }];
    return entries.map(([name, value]) => ({ name, value: value ?? '' }));
}

const ListTreeItemsEditor = ({ listTreeID, listTreeName, items, loading, onReload }) => {
    const [expanded, setExpanded] = useState(() => new Set());
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [editItemId, setEditItemId] = useState(null);
    const [fixedParentId, setFixedParentId] = useState(null);
    const [formValue, setFormValue] = useState('');
    const [formCode, setFormCode] = useState('');
    const [formParentId, setFormParentId] = useState('');
    const [attrRows, setAttrRows] = useState([{ name: '', value: '' }]);

    const childrenByParent = useMemo(() => {
        const m = new Map();
        items.forEach(row => {
            const pid = itemParentId(row);
            const key = pid == null ? null : pid;
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(row);
        });
        m.forEach(list => {
            list.sort((a, b) => {
                const cmp = itemValue(a).localeCompare(itemValue(b), undefined, { sensitivity: 'base' });
                if (cmp !== 0) return cmp;
                return itemId(a) - itemId(b);
            });
        });
        return m;
    }, [items]);

    const roots = childrenByParent.get(null) || [];

    useEffect(() => {
        if (!items.length) return;
        setExpanded(prev => {
            if (prev.size > 0) return prev;
            const next = new Set();
            items.forEach(row => {
                const id = itemId(row);
                const kids = childrenByParent.get(id);
                if (kids && kids.length) next.add(id);
            });
            return next;
        });
    }, [items, childrenByParent]);

    const openNewRoot = () => {
        setModalMode('new');
        setEditItemId(null);
        setFixedParentId(null);
        setFormValue('');
        setFormCode('');
        setFormParentId('');
        setAttrRows([{ name: '', value: '' }]);
        setSaveModalOpen(true);
    };

    const openNewChild = parentRow => {
        const pid = itemId(parentRow);
        setModalMode('new');
        setEditItemId(null);
        setFixedParentId(pid);
        setFormValue('');
        setFormCode('');
        setFormParentId(String(pid));
        setAttrRows([{ name: '', value: '' }]);
        setSaveModalOpen(true);
    };

    const openEdit = row => {
        setModalMode('edit');
        setEditItemId(itemId(row));
        setFixedParentId(null);
        setFormValue(itemValue(row));
        setFormCode(itemCode(row) ?? '');
        const p = itemParentId(row);
        setFormParentId(p == null ? '' : String(p));
        setAttrRows(attrsToRows(itemAttrs(row)));
        setSaveModalOpen(true);
    };

    const closeModal = () => {
        setSaveModalOpen(false);
    };

    const parentOptions = useMemo(() => {
        const excluded = new Set();
        if (modalMode === 'edit' && editItemId != null) {
            excluded.add(editItemId);
            collectDescendants(items, editItemId).forEach(id => excluded.add(id));
        }
        return items
            .filter(r => !excluded.has(itemId(r)))
            .map(r => ({
                id: itemId(r),
                label: `${itemValue(r)} (#${itemId(r)})`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    }, [items, modalMode, editItemId]);

    const parseParentId = () => {
        if (fixedParentId != null) return fixedParentId;
        const s = formParentId.trim();
        if (s === '') return null;
        const n = parseInt(s, 10);
        return Number.isNaN(n) ? null : n;
    };

    const handleSaveItem = async () => {
        const v = formValue.trim();
        if (!v) {
            exceptionNotificationAPI({ response: { data: { message: 'Value is required.' } } });
            return;
        }
        const attrs = {};
        attrRows.forEach(({ name, value: val }) => {
            const n = (name || '').trim();
            if (n) attrs[n] = val ?? '';
        });
        const parentID = parseParentId();
        const codeTrim = formCode.trim();
        setSaving(true);
        try {
            await Api.post('ListTree/saveItem', {
                listTreeID: Number(listTreeID),
                listTreeItemID: modalMode === 'edit' && editItemId != null ? editItemId : 0,
                parentID,
                value: v,
                code: codeTrim === '' ? null : codeTrim,
                attrs,
            });
            success_message('Item saved');
            closeModal();
            onReload();
        } catch (e) {
            exceptionNotificationAPI(e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = useCallback(
        async row => {
            const id = itemId(row);
            const label = itemValue(row);
            const ok = window.confirm(
                `Delete "${label}" and all nested items under it? This cannot be undone from the UI.`,
            );
            if (!ok) return;
            try {
                await Api.post('ListTree/deleteItem', { listTreeItemID: id });
                success_message('Item deleted');
                onReload();
            } catch (e) {
                exceptionNotificationAPI(e);
            }
        },
        [onReload],
    );

    const toggleExpand = id => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const renderNode = (row, depth) => {
        const id = itemId(row);
        const children = childrenByParent.get(id) || [];
        const hasKids = children.length > 0;
        const isOpen = expanded.has(id);
        const pill = pillText(row);

        return (
            <React.Fragment key={id}>
                <TreeRow $depth={depth}>
                    <TreeRowMain>
                        <TreeExpandSlot>
                            {hasKids ? (
                                <TreeExpandBtn
                                    type="button"
                                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                                    onClick={() => toggleExpand(id)}
                                >
                                    {isOpen ? '−' : '+'}
                                </TreeExpandBtn>
                            ) : (
                                <span style={{ width: 22, display: 'inline-block' }} />
                            )}
                        </TreeExpandSlot>
                        <TreeIconSlot>{hasKids ? isOpen ? <FaFolderOpen /> : <FaFolder /> : <FaFileAlt />}</TreeIconSlot>
                        <TreeLabel>{itemValue(row)}</TreeLabel>
                    </TreeRowMain>
                    <TreeActionsRail>
                        {pill ? <TypePill>{pill}</TypePill> : <span style={{ minWidth: 88 }} />}
                        <IconBtn type="button" title="Edit" onClick={() => openEdit(row)}>
                            <FaPencilAlt />
                        </IconBtn>
                        <IconBtn type="button" title="Add child" onClick={() => openNewChild(row)}>
                            <FaPlus />
                        </IconBtn>
                        <IconBtn type="button" title="Delete branch" onClick={() => handleDelete(row)}>
                            <FaTrash />
                        </IconBtn>
                    </TreeActionsRail>
                </TreeRow>
                {hasKids && isOpen && children.map(ch => renderNode(ch, depth + 1))}
            </React.Fragment>
        );
    };

    const showParentSelect = fixedParentId == null;

    return (
        <>
            <TreeShell>
                <TreeHeaderBar>
                    <TreeHeaderTitle>
                        <FaFolder style={{ opacity: 0.95 }} />
                        <span>{listTreeName || 'List tree'}</span>
                    </TreeHeaderTitle>
                    <TreeHeaderActions>
                        <TreeHeaderIconBtn type="button" title="Add root item" onClick={openNewRoot}>
                            <FaPlus />
                        </TreeHeaderIconBtn>
                    </TreeHeaderActions>
                </TreeHeaderBar>
                <TreeBodyPanel>
                    {loading ? (
                        <EmptyTreeHint>Loading items…</EmptyTreeHint>
                    ) : items.length === 0 ? (
                        <EmptyTreeHint>No items yet. Add a root item or import JSON below.</EmptyTreeHint>
                    ) : (
                        roots.map(r => renderNode(r, 0))
                    )}
                </TreeBodyPanel>
            </TreeShell>

            <Modal show={saveModalOpen} onHide={closeModal} size="lg" centered>
                <ModalHeader closeButton>
                    <ModalTitle>
                        {modalMode === 'edit' ? 'Edit item' : fixedParentId != null ? 'New child item' : 'New root item'}
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <label className="form-label">Value</label>
                        <input
                            className="form-control"
                            value={formValue}
                            onChange={e => setFormValue(e.target.value)}
                            placeholder="Display label"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Code</label>
                        <input
                            className="form-control"
                            value={formCode}
                            onChange={e => setFormCode(e.target.value)}
                            placeholder="Optional code"
                        />
                    </div>
                    {showParentSelect && (
                        <div className="mb-3">
                            <label className="form-label">Parent</label>
                            <select
                                className="form-select"
                                value={formParentId}
                                onChange={e => setFormParentId(e.target.value)}
                            >
                                <option value="">(root)</option>
                                {parentOptions.map(o => (
                                    <option key={o.id} value={String(o.id)}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {!showParentSelect && (
                        <div className="mb-3 text-muted small">Parent is set to the selected tree node.</div>
                    )}
                    <div className="mb-2 fw-semibold">Attributes</div>
                    <p className="text-muted small mb-2">Optional key/value pairs (e.g. unitMeasure, Functionality).</p>
                    {attrRows.map((row, idx) => (
                        <div className="d-flex gap-2 mb-2" key={idx}>
                            <input
                                className="form-control"
                                placeholder="Name"
                                value={row.name}
                                onChange={e => {
                                    const next = [...attrRows];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    setAttrRows(next);
                                }}
                            />
                            <input
                                className="form-control"
                                placeholder="Value"
                                value={row.value}
                                onChange={e => {
                                    const next = [...attrRows];
                                    next[idx] = { ...next[idx], value: e.target.value };
                                    setAttrRows(next);
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setAttrRows(attrRows.filter((_, i) => i !== idx))}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setAttrRows([...attrRows, { name: '', value: '' }])}
                    >
                        Add attribute row
                    </button>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSaveItem} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ListTreeItemsEditor;
